import {faGear} from "@fortawesome/pro-solid-svg-icons/faGear";
import {faImageUser} from "@fortawesome/pro-solid-svg-icons/faImageUser";
import {faRightFromBracket} from "@fortawesome/pro-solid-svg-icons/faRightFromBracket";
import {faCaretDown} from "@fortawesome/pro-solid-svg-icons/faCaretDown";
import {useClientAccountOverview} from "@twigsby/client-data";
import {
    AvatarSize, ColourSchemeNames,
    DropdownMenu,
    ItemMenuEntry,
    ToggleSwitch,
    ToggleType, Tooltip, ToolTipPlacement,
    useLayout
} from "@twigsby/frontend-framework";
import {FormatCurrency} from "@twigsby/helpers";
import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCloudMoon} from "@fortawesome/pro-solid-svg-icons/faCloudMoon";
import {faCompress} from "@fortawesome/pro-solid-svg-icons/faCompress";
import {faCreditCard} from "@fortawesome/pro-solid-svg-icons/faCreditCard";
import {faEllipsisVertical} from "@fortawesome/pro-solid-svg-icons/faEllipsisVertical";
import {useTranslation} from "@twigsby/frontend-framework";
import {useNavigate} from "react-router-dom";
import {useUKPBAuth} from "../Providers/AuthProvider";
import {useSite} from "../Providers/SiteInterfaceProvider";
import {useUser} from "../Providers/UserProvider";
import {UserAvatar} from "./User/UserAvatar";

type UserMenuType = {
    inHeader?: boolean;
    avatarSize?: AvatarSize;
    expanded?: boolean;
}

type UserMenuDropdownType = {
    activatorClass?: string;
    avatarSize?: AvatarSize;
    expanded?: boolean;
    customTrigger?: any;
}

type UserMetaType = {
    inline?: boolean;
}

const UserMenu = (props: UserMenuType) => {
    const { t } = useTranslation();
    const { sidebarOpen, isMedium, isUltraWide} = useLayout();

    return (
        <Tooltip content={t('actions.manageAccount')} position={ToolTipPlacement.Right} disabled={sidebarOpen || !isMedium || isUltraWide}>
            <div className={'side-navigation-entry-user'}>
                <UserMenuDropdownMenu activatorClass={'user-overview-nav'} avatarSize={props.avatarSize} expanded={props.expanded} />
            </div>
        </Tooltip>
    )
}

const UserMeta = (props: UserMetaType) => {
    const {data} = useClientAccountOverview();

    return (
        <div className={`user-mini-overview-meta ${props.inline ? 'user-mini-overview-meta-inline' : '' }`}>
            <div className={'user-mini-name'}>{data?.name}</div>
            <span className={'user-mini-balance'}>{FormatCurrency(data?.balance || 0)}</span>
        </div>
    )
}

const UserMenuDropdownMenu = (props: UserMenuDropdownType) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {data} = useClientAccountOverview();
    const { sizeMode, updateSizeMode, sidebarOpen, isUltraWide, theme, localTheme, updateTheme } = useLayout();
    const { SelectNewAvatar } = useUser();

    const { LogOut } = useUKPBAuth();

    const CycleTheme = () => {
        if (theme === ColourSchemeNames.AUTO) {
            updateTheme(ColourSchemeNames.LIGHT);
        }
        if (theme === ColourSchemeNames.LIGHT) {
            updateTheme(ColourSchemeNames.DARK);
        }
        if (theme === ColourSchemeNames.DARK) {
            updateTheme(ColourSchemeNames.AUTO);
        }
    };

    return (
        <DropdownMenu
            trigger={
                props.customTrigger ? (
                    <>
                        {props.customTrigger}
                    </>
                ) : (
                    <div
                        className={`user-overview-nav side-navigation-entry side-navigation-user-entry collapsable ${props.expanded ? 'user-overview-expanded' : ''}`}
                        tabIndex={0}>
                        <div className={`user-mini-overview`}>
                            <UserAvatar size={props.avatarSize ? props.avatarSize : AvatarSize.Medium}/>
                            {(props.expanded || sidebarOpen || isUltraWide) && (
                                <>
                                    <UserMeta inline={props.expanded}/>
                                    <FontAwesomeIcon
                                        icon={props.expanded ? faCaretDown : faEllipsisVertical}
                                        fixedWidth={true}
                                    />

                                </>
                            )}
                        </div>
                    </div>
                )
            }
        >

            {data && (
                <ItemMenuEntry title={t('actions.topUp')} onClick={() => navigate('/manage-account/payment/top-up')}
                               icon={faCreditCard}
                               addonContent={<small className={'fade'}>{FormatCurrency(data?.balance)}</small>}/>
            )}

            <ItemMenuEntry title={t('actions.settings')} onClick={() => navigate('/manage-account')}
                           icon={faGear}/>

            <ItemMenuEntry title={t('labels.divider')} sectionDivider={true}/>

            <ItemMenuEntry title={t('actions.logOut')} onClick={() => LogOut()} icon={faRightFromBracket}/>

            <ItemMenuEntry title={t('labels.divider')} sectionDivider={true}/>

            <ItemMenuEntry title={t('labels.preferences')} sectionHeader={true} />

            <ItemMenuEntry
                title={t('framework.labels.colourScheme')}
                onClick={(e) => {
                    e.stopPropagation();
                    CycleTheme();
                }}
                icon={faCloudMoon}
                addonContent={
                    <div className={`colour-scheme-wrapper ${localTheme}-active`}>
                        <span className="auto">{t('framework.labels.auto')}</span>
                        <span className="sun"/>
                        <span className="moon"/>
                    </div>
                }
            />

            <ItemMenuEntry
                title={t('actions.compactMode')}
                onClick={() => updateSizeMode(sizeMode !== 'small' ? 'small' : '')}
                icon={faCompress}
                addonContent={<ToggleSwitch type={ToggleType.Switch} label={t('actions.compactMode')}
                                            name={'compactMode'} defaultChecked={sizeMode === 'small'}
                                            hideLabel={true}/>}
            />

            <ItemMenuEntry
                title={t('actions.largeMode')}
                onClick={() => updateSizeMode(sizeMode !== 'large' ? 'large' : '')}
                icon={faCompress}
                addonContent={<ToggleSwitch type={ToggleType.Switch} label={t('actions.largeMode')}
                                            name={'largeMode'} defaultChecked={sizeMode === 'large'} hideLabel={true}/>}
            />
        </DropdownMenu>
    )
}

export default UserMenu

export { UserMenuDropdownMenu }
